import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { app } from "../../../firebaseFunctions/config";
import { ExternalLink } from "lucide-react";
import { isDemoMode } from "../../../demo/isDemoMode";
import demoStore from "../../../demo/demoStore";

const PLATFORMS = [
  { key: "instagram", label: "Instagram",  placeholder: "@yourbusiness",       prefix: "instagram.com/",  color: "bg-pink-500" },
  { key: "tiktok",    label: "TikTok",     placeholder: "@yourbusiness",       prefix: "tiktok.com/@",   color: "bg-black" },
  { key: "facebook",  label: "Facebook",   placeholder: "facebook.com/yourpage", prefix: "",             color: "bg-blue-600" },
  { key: "twitter",   label: "X / Twitter", placeholder: "@yourbusiness",     prefix: "x.com/",          color: "bg-grey-900" },
  { key: "youtube",   label: "YouTube",    placeholder: "@yourchannel",        prefix: "youtube.com/@",  color: "bg-red-600" },
  { key: "pinterest", label: "Pinterest",  placeholder: "@yourbusiness",       prefix: "pinterest.com/", color: "bg-red-500" },
  { key: "linkedin",  label: "LinkedIn",   placeholder: "linkedin.com/in/you", prefix: "",              color: "bg-blue-700" },
];

const emptyState = () => Object.fromEntries(PLATFORMS.map(p => [p.key, ""]));

const parseLinksToState = (links) => {
  const state = emptyState();
  if (!links) return state;
  const entries = Array.isArray(links)
    ? links.flatMap(obj => Object.entries(obj))
    : Object.entries(links);
  entries.forEach(([platform, handle]) => {
    const match = PLATFORMS.find(p =>
      p.key === platform.toLowerCase() || platform.toLowerCase().includes(p.key)
    );
    if (match) state[match.key] = handle;
  });
  return state;
};

const stateToLinks = (state) =>
  PLATFORMS
    .filter(p => state[p.key]?.trim())
    .map(p => ({ [p.key]: state[p.key].trim() }));

const EOSocialMediaContent = () => {
  const { user } = useAuth();
  const [handles, setHandles] = useState(emptyState());
  const [draft, setDraft] = useState(emptyState());
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!user) return;
      setLoading(true);
      try {
        let links = null;
        if (isDemoMode) {
          const profile = demoStore.getDoc("EO_BusinessProfiles", user.uid);
          links = profile?.socialMediaLinks;
        } else {
          const db = getFirestore(app);
          const snap = await getDoc(doc(db, "EO_BusinessProfiles", user.uid));
          if (snap.exists()) links = snap.data().socialMediaLinks;
        }
        const parsed = parseLinksToState(links);
        setHandles(parsed);
        setDraft(parsed);
      } catch (e) {
        console.error("Error fetching EO social links:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const links = stateToLinks(draft);
      if (isDemoMode) {
        demoStore.updateDoc("EO_BusinessProfiles", user.uid, { socialMediaLinks: links });
      } else {
        const db = getFirestore(app);
        await updateDoc(doc(db, "EO_BusinessProfiles", user.uid), { socialMediaLinks: links });
      }
      setHandles(draft);
      setIsEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error("Error saving EO social links:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(handles);
    setIsEditing(false);
  };

  const filledCount = PLATFORMS.filter(p => handles[p.key]?.trim()).length;

  if (loading) return <div className="text-sm text-grey-500 py-4">Loading...</div>;

  return (
    <div className="w-full max-w-lg">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-grey-700">Social Media</p>
          <p className="text-xs text-grey-400 mt-0.5">
            {filledCount > 0
              ? `${filledCount} platform${filledCount !== 1 ? "s" : ""} connected — shown on your public event pages`
              : "Shown on your public event pages"}
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={() => { setDraft({ ...handles }); setIsEditing(true); }}
            className="px-4 py-2 text-sm font-medium bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition"
          >
            Edit
          </button>
        )}
      </div>

      {saved && (
        <div className="mb-4 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 font-medium">
          Social links saved.
        </div>
      )}

      <div className="space-y-3 mt-4">
        {PLATFORMS.map(p => {
          const value = isEditing ? draft[p.key] : handles[p.key];
          const hasValue = value?.trim();
          if (!isEditing && !hasValue) return null;

          return (
            <div key={p.key} className={`flex items-center gap-3 p-3 rounded-xl border transition ${
              isEditing ? "border-grey-200 bg-white" : "border-grey-100 bg-grey-50"
            }`}>
              <div className={`w-8 h-8 rounded-lg ${p.color} flex items-center justify-center flex-shrink-0`}>
                <span className="text-white text-[10px] font-black uppercase">{p.label.slice(0, 2)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-grey-600 mb-1">{p.label}</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={draft[p.key]}
                    onChange={e => setDraft(prev => ({ ...prev, [p.key]: e.target.value }))}
                    placeholder={p.placeholder}
                    className="w-full px-2.5 py-1.5 text-sm border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent"
                  />
                ) : (
                  <p className="text-sm text-grey-800 font-medium truncate">{value}</p>
                )}
              </div>
              {!isEditing && hasValue && p.prefix && (
                <a
                  href={`https://${p.prefix}${value.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-grey-400 hover:text-grey-600 flex-shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      {!isEditing && filledCount === 0 && (
        <div className="text-center py-10 text-grey-400">
          <p className="text-sm mb-3">Add your social handles — they'll appear on every event page you publish.</p>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-sm font-medium bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition"
          >
            Add Social Links
          </button>
        </div>
      )}

      {isEditing && (
        <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-grey-100">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium border border-grey-300 rounded-lg text-grey-700 hover:bg-grey-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EOSocialMediaContent;
